import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Dropdown,
  Modal,
  message,
  Row,
  Col,
  Statistic,
  Tooltip,
  Empty
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import reportService from '../services/reportService';
import useAuthStore from '../store/authStore';
import CreateReportModal from '../components/Reports/CreateReportModal';
import ReportDetailView from '../components/Reports/ReportDetailView';
import PageIcon from '../components/Layout/PageIcon';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const ReportsPage = () => {
  const { user } = useAuthStore();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedVisibility, setSelectedVisibility] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [stats, setStats] = useState({});

  // Fetch reports on component mount
  useEffect(() => {
    fetchReports();
    fetchStats();
  }, []);

  // Filter reports when search or filters change
  useEffect(() => {
    filterReports();
  }, [reports, searchText, selectedType, selectedCategory, selectedVisibility]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await reportService.getReports();
      if (response.success) {
        setReports(response.data.reports);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      message.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await reportService.getReportStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const filterReports = () => {
    let filtered = [...reports];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(report =>
        report.name.toLowerCase().includes(searchText.toLowerCase()) ||
        report.description?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Type filter
    if (selectedType) {
      filtered = filtered.filter(report => report.type === selectedType);
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(report => report.category === selectedCategory);
    }

    // Visibility filter
    if (selectedVisibility) {
      filtered = filtered.filter(report => report.visibility === selectedVisibility);
    }

    setFilteredReports(filtered);
  };

  const handleCreateReport = () => {
    setCreateModalVisible(true);
  };


  const handleReportCreated = (newReport) => {
    setReports(prev => [newReport, ...prev]);
    setCreateModalVisible(false);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowReportDetail(true);
  };

  const handleBackToReports = () => {
    setSelectedReport(null);
    setShowReportDetail(false);
  };

  const handleGenerateReport = async (report) => {
    try {
      setLoading(true);
      const response = await reportService.generateReport(report._id);
      if (response.success) {
        message.success('Report generated successfully');
        // You could open a new modal/page to show the generated data
        console.log('Generated report data:', response.data);
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      message.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleEditReport = (report) => {
    // TODO: Implement edit functionality
    message.info('Edit functionality coming soon');
  };

  const handleDeleteReport = async (report) => {
    Modal.confirm({
      title: 'Delete Report',
      content: `Are you sure you want to delete "${report.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await reportService.deleteReport(report._id);
          setReports(prev => prev.filter(r => r._id !== report._id));
          message.success('Report deleted successfully');
        } catch (error) {
          console.error('Failed to delete report:', error);
          message.error('Failed to delete report');
        }
      }
    });
  };

  const handleShareReport = (report) => {
    // TODO: Implement share functionality
    message.info('Share functionality coming soon');
  };

  const handleDuplicateReport = async (report) => {
    try {
      const response = await reportService.duplicateReport(report._id, `${report.name} - Copy`);
      if (response.success) {
        setReports(prev => [response.data, ...prev]);
        message.success('Report duplicated successfully');
      } else {
        message.error('Failed to duplicate report');
      }
    } catch (error) {
      console.error('Failed to duplicate report:', error);
      message.error('Failed to duplicate report');
    }
  };

  const getReportTypeIcon = (type) => {
    const icons = {
      leads: <BarChartOutlined />,
      deals: <BarChartOutlined />,
      activities: <FileTextOutlined />,
      performance: <BarChartOutlined />,
      custom: <SettingOutlined />
    };
    return icons[type] || <FileTextOutlined />;
  };

  const getReportTypeColor = (type) => {
    const colors = {
      leads: 'blue',
      deals: 'green',
      activities: 'orange',
      performance: 'purple',
      custom: 'default'
    };
    return colors[type] || 'default';
  };

  const getVisibilityColor = (visibility) => {
    const colors = {
      private: 'default',
      team: 'blue',
      tenant: 'green'
    };
    return colors[visibility] || 'default';
  };

  const canEditReport = (report) => {
    return report.createdBy._id === user._id || user.role === 'tenant_admin';
  };

  const canDeleteReport = (report) => {
    return report.createdBy._id === user._id || user.role === 'tenant_admin';
  };

  const getActionMenu = (report) => ({
    items: [
      {
        key: 'view',
        icon: <PlayCircleOutlined />,
        label: 'View Details',
        onClick: () => handleViewReport(report)
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        onClick: () => handleDeleteReport(report),
        disabled: !canDeleteReport(report),
        danger: true
      }
    ]
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {getReportTypeIcon(record.type)}
          <div>
            <div className="font-medium">{text}</div>
            {record.description && (
              <div className="text-gray-500 text-sm">{record.description}</div>
            )}
          </div>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={getReportTypeColor(type)}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Tag>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag>{category.charAt(0).toUpperCase() + category.slice(1)}</Tag>
      )
    },
    {
      title: 'Visibility',
      dataIndex: 'visibility',
      key: 'visibility',
      render: (visibility) => (
        <Tag color={getVisibilityColor(visibility)}>
          {visibility.charAt(0).toUpperCase() + visibility.slice(1)}
        </Tag>
      )
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy) => (
        <div>
          <div>{createdBy.firstName} {createdBy.lastName}</div>
          <div className="text-gray-500 text-sm">{createdBy.email}</div>
        </div>
      )
    },
    {
      title: 'Last Generated',
      dataIndex: 'lastGenerated',
      key: 'lastGenerated',
      render: (date) => (
        date ? (
          <Tooltip title={new Date(date).toLocaleString()}>
            {formatDistanceToNow(new Date(date), { addSuffix: true })}
          </Tooltip>
        ) : (
          <Text type="secondary">Never</Text>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<PlayCircleOutlined />}
            onClick={() => handleViewReport(record)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<BarChartOutlined />}
            onClick={() => handleGenerateReport(record)}
            title="Generate Report"
          />
          <Dropdown menu={getActionMenu(record)} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  // Show report detail view if a report is selected
  if (showReportDetail && selectedReport) {
    return (
      <ReportDetailView
        visible={true}
        report={selectedReport}
        onClose={handleBackToReports}
        onReportUpdated={fetchReports}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <Title level={2} className="mb-2" style={{ display: 'flex', alignItems: 'center' }}>
              <PageIcon name="reports" /> Reports
            </Title>
            <Text className="text-gray-600">
              Create and manage custom reports based on your CRM data
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateReport}
            size="large"
          >
            Create Report
          </Button>
        </div>

        {/* Statistics */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Reports"
                value={stats.totalReports || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Generations"
                value={stats.totalGenerations || 0}
                prefix={<PlayCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Avg Generation Time"
                value={stats.avgGenerationTime ? `${stats.avgGenerationTime.toFixed(1)}s` : '0s'}
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="My Reports"
                value={stats.myReports || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search reports..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Type"
              value={selectedType}
              onChange={setSelectedType}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="leads">Leads</Option>
              <Option value="deals">Deals</Option>
              <Option value="activities">Activities</Option>
              <Option value="performance">Performance</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Category"
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="sales">Sales</Option>
              <Option value="marketing">Marketing</Option>
              <Option value="performance">Performance</Option>
              <Option value="analytics">Analytics</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Visibility"
              value={selectedVisibility}
              onChange={setSelectedVisibility}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="private">Private</Option>
              <Option value="team">Team</Option>
              <Option value="tenant">Organization</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Reports Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredReports}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} reports`
          }}
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => handleViewReport(record),
            style: { cursor: 'pointer' },
            className: 'hover:bg-gray-50'
          })}
          locale={{
            emptyText: (
              <Empty
                description="No reports found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={handleCreateReport}>
                  Create Your First Report
                </Button>
              </Empty>
            )
          }}
        />
      </Card>

      {/* Create Report Modal */}
      <CreateReportModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={handleReportCreated}
      />

    </div>
  );
};

export default ReportsPage;
